import type { AIModel } from "@saas/ai-image-generator/types";

/**
 * 模型下拉按它分组、配品牌图标（ModelMenuItems）。v3.0 是站内默认模型，用站内
 * logo 单独放最上面（老公 10/19 定，保留 V3.0 这个名字）。
 */
export type ModelBrand =
	| "imagesart"
	| "seedream"
	| "nano-banana"
	| "gpt"
	| "qwen";

/**
 * 共享的 AI 图像模型列表配置
 * ai-image-generator 与 ai-edit-image 都从这里读取，确保两边模型选项一致
 */
export type AiModelListItem = {
	id: AIModel;
	name: string;
	brand: ModelBrand;
	/** 下拉里名字下面的短标签，替代原来的一行描述（10/19）。分辨率直接写 2K/4K，不翻译 */
	tags: string[];
	badge?: string;
	/**
	 * 下拉里不列，但留在清单里：旧历史记录要显示它的名字，只允许它的模板
	 * （模板 behavior.model.allowed 点名的）也还要能选到它。
	 */
	hidden?: boolean;
};

/**
 * 返回 AI 模型清单（带 i18n 文案），含 hidden 的。下拉框用 getAiModelPickerList，
 * 按 id 查名字（历史卡片的模型名）用这个。
 * 调用方传入 next-intl 的翻译函数 t（命名空间默认为 ai-image-generator）。
 */
export function getAiModelList(t: (key: string) => string): AiModelListItem[] {
	return [
		// v2.0 暂时下架，只从下拉里隐藏；后端仍保留，旧历史记录重新生成不会 400。
		// {
		// 	id: "v2.0",
		// 	name: t("imageSettings.models.v2_0.name"),
		// 	description: t("imageSettings.models.v2_0.description"),
		// },
		{
			// v3.0 底层已换成 kie 的 Seedream 5 Lite（原 fal Seedream v4）
			id: "v3.0",
			name: t("imageSettings.models.v3_0.name"),
			brand: "imagesart",
			tags: [t("imageSettings.modelTags.multiImage"), "4K"],
		},
		{
			id: "seedream-5-flash",
			name: t("imageSettings.models.seedream-5-flash.name"),
			brand: "seedream",
			tags: [
				t("imageSettings.modelTags.fast"),
				t("imageSettings.modelTags.lowCost"),
			],
			badge: t("imageSettings.models.seedream-5-flash.badge"),
		},
		{
			id: "seedream-5-pro",
			name: t("imageSettings.models.seedream-5-pro.name"),
			brand: "seedream",
			tags: [t("imageSettings.modelTags.richDetail"), "2K"],
			badge: t("imageSettings.models.seedream-5-pro.badge"),
		},
		{
			id: "qwen-image-3",
			name: t("imageSettings.models.qwen-image-3.name"),
			brand: "qwen",
			tags: [
				t("imageSettings.modelTags.textRendering"),
				t("imageSettings.modelTags.layouts"),
			],
			badge: t("imageSettings.models.qwen-image-3.badge"),
		},
		{
			id: "qwen-image-3-pro",
			name: t("imageSettings.models.qwen-image-3-pro.name"),
			brand: "qwen",
			tags: [t("imageSettings.modelTags.fineDetail"), "2K"],
			badge: t("imageSettings.models.qwen-image-3-pro.badge"),
		},
		{
			id: "gpt-image-2",
			name: t("imageSettings.models.gpt-image-2.name"),
			brand: "gpt",
			tags: [
				t("imageSettings.modelTags.promptFollowing"),
				t("imageSettings.modelTags.textRendering"),
			],
		},
		{
			// 2026-10-18 起下拉里由 nano-banana-2-1 替换，见下一条
			id: "nano-banana-2",
			name: t("imageSettings.models.nano-banana-2.name"),
			brand: "nano-banana",
			tags: [t("imageSettings.modelTags.multiImage"), "4K"],
			badge: t("imageSettings.models.nano-banana-2.badge"),
			hidden: true,
		},
		{
			id: "nano-banana-2-1",
			name: t("imageSettings.models.nano-banana-2-1.name"),
			brand: "nano-banana",
			tags: [t("imageSettings.modelTags.multiImage"), "4K"],
			badge: t("imageSettings.models.nano-banana-2-1.badge"),
		},
		{
			id: "nano-banana-pro",
			name: t("imageSettings.models.nano-banana-pro.name"),
			brand: "nano-banana",
			tags: [t("imageSettings.modelTags.bestQuality"), "4K"],
		},
	];
}

/**
 * 下拉框里列的模型：去掉 hidden 的，除非模板点名允许它（allowed 里有就照列）。
 */
export function getAiModelPickerList(
	models: AiModelListItem[],
	allowed?: readonly AIModel[],
): AiModelListItem[] {
	if (allowed && allowed.length > 0) {
		return models.filter((model) => allowed.includes(model.id));
	}
	return models.filter((model) => !model.hidden);
}
